<div>
  <Navbar />
  <Sidebar />

  <div class="page-wrapper">
    <div class="hero-section w-100">
      <div class="border border-secondary rounded-4 p-2">
        <div class="card rounded-4 shadow-sm platform-side-card mb-4">
          <div class="card-body d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <p class="platform-page-kicker mb-2">Platform Management</p>
              <h2 class="gradient-text fw-bold mb-1">Add Platform Config</h2>
              <p class="text-muted mb-0">Connect an external platform to AIC One for secure launch, readiness checks, DevOps handover, and SSO access.</p>
            </div>
            <button class="upload-button " type="button" @click="go_back_to_platform_management">
              Back to Platform Management
            </button>
          </div>
        </div>

        <div class="card rounded-4 shadow-sm table-card">
          <div class="card-body">
            <div class="d-flex flex-wrap gap-2 mb-4">
              <button
                type="button"
                class="btn btn-sm rounded-pill px-3"
                :class="platform_config_step === 1 ? 'upload-button text-white' : 'btn-outline-dark'"
                @click="go_to_step(1)"
              >
                1. Identity
              </button>
              <button
                type="button"
                class="btn btn-sm rounded-pill px-3"
                :class="platform_config_step === 2 ? 'upload-button text-white' : 'btn-outline-dark'"
                @click="go_to_step(2)"
              >
                2. URLs
              </button>
              <button
                type="button"
                class="btn btn-sm rounded-pill px-3"
                :class="platform_config_step === 3 ? 'upload-button text-white' : 'btn-outline-dark'"
                @click="go_to_step(3)"
              >
                3. Security
              </button>
              <button
                type="button"
                class="btn btn-sm rounded-pill px-3"
                :class="platform_config_step === 4 ? 'upload-button text-white' : 'btn-outline-dark'"
                @click="go_to_step(4)"
              >
                4. Advanced
              </button>
            </div>

            <form @submit.prevent="submit_platform_config">
              <div v-if="platform_config_step === 1" class="border rounded-4 p-3 bg-light-subtle">
                <div class="mb-3 text-start">
                  <label class="ms-2 d-flex align-items-center gap-2">
                    <span>Platform</span>
                    <span class="badge text-bg-danger">required</span>
                  </label>
                  <select v-model="platform_config_form.platform_id" class="form-control" required @change="handle_platform_change">
                    <option value="" disabled>Select platform</option>
                    <option v-for="platform_option in platform_options" :key="platform_option.id" :value="platform_option.id">
                      {{ platform_option.label }}
                    </option>
                  </select>
                  <p class="small text-muted mb-0 mt-2 px-2">Choose the platform you are connecting to AIC One.</p>
                </div>

                <div class="mb-3 text-start">
                  <label class="ms-2 d-flex align-items-center gap-2">
                    <span>App Code</span>
                    <span class="badge text-bg-danger">required</span>
                  </label>
                  <input v-model="platform_config_form.app_code" type="text" class="form-control" placeholder="e.g. Proba Analytics or EM Guidance" required>
                  <p class="small text-muted mb-0 mt-2 px-2">This is the identifier AIC One uses for routing, launch, and DevOps handover.</p>
                </div>

                <div class="mb-0 text-start">
                  <label class="ms-2 d-flex align-items-center gap-2">
                    <span>Environment</span>
                    <span class="badge text-bg-danger">required</span>
                  </label>
                  <select v-model="platform_config_form.environment" class="form-control">
                    <option value="development">development</option>
                    <option value="staging">staging</option>
                    <option value="uat">uat</option>
                    <option value="demo">demo</option>
                    <option value="production">production</option>
                  </select>
                  <p class="small text-muted mb-0 mt-2 px-2">Choose where this platform config will be used. Avoid production until testing is complete.</p>
                </div>
              </div>

              <div v-if="platform_config_step === 2" class="border rounded-4 p-3 bg-light-subtle">
                <div class="mb-3 text-start">
                  <label class="ms-2 d-flex align-items-center gap-2">
                    <span>Base URL</span>
                    <span class="badge text-bg-danger">required</span>
                  </label>
                  <input v-model="platform_config_form.base_url" type="url" class="form-control" placeholder="https://platform-domain/api" required>
                  <p class="small text-muted mb-0 mt-2 px-2">The main API base URL AIC One will use to call this platform.</p>
                </div>

                <div class="mb-3 text-start">
                  <label class="ms-2 d-flex align-items-center gap-2">
                    <span>Launch URL</span>
                    <span class="badge text-bg-danger">required</span>
                  </label>
                  <input v-model="platform_config_form.launch_url" type="url" class="form-control" placeholder="https://platform-domain/sso/assert" required>
                  <p class="small text-muted mb-0 mt-2 px-2">The SSO assert URL where AIC One redirects users when they launch this platform.</p>
                </div>

                <div class="mb-3 text-start">
                  <label class="ms-2 d-flex align-items-center gap-2">
                    <span>Callback URL</span>
                    <span class="badge text-bg-secondary">optional</span>
                  </label>
                  <input v-model="platform_config_form.callback_url" type="url" class="form-control" placeholder="https://aic-one-domain/callback">
                  <p class="small text-muted mb-0 mt-2 px-2">Optional URL used if the platform needs to send users or responses back to AIC One.</p>
                </div>

                <div class="border rounded-4 p-3 bg-white">
                  <p class="fw-semibold mb-1">How launch works</p>
                  <p class="small text-muted mb-0">AIC One syncs the user, signs an SSO assertion, and redirects the browser to the platform Launch URL.</p>
                </div>
              </div>

              <div v-if="platform_config_step === 3" class="border rounded-4 p-3 bg-light-subtle">
                <div class="alert alert-info small mb-3">
                  Sensitive deployment values are generated securely by AIC One when this configuration is saved. They are not entered on this page.
                </div>

                <div class="mb-3 text-start">
                  <label class="ms-2 d-flex align-items-center gap-2">
                    <span>Signer Salt</span>
                    <span class="badge text-bg-danger">required</span>
                  </label>
                  <input v-model="platform_config_form.signer_salt" type="text" class="form-control" placeholder="e.g. em_guidance-aic-one">
                  <p class="small text-muted mb-0 mt-2 px-2">Used to verify signed launch assertions between AIC One and the platform.</p>
                </div>

                <div class="mb-3 text-start">
                  <label class="ms-2 d-flex align-items-center gap-2">
                    <span>Max Allowed Skew</span>
                    <span class="badge text-bg-danger">required</span>
                  </label>
                  <input v-model="platform_config_form.max_allowed_skew" type="number" class="form-control" min="0" step="1">
                  <p class="small text-muted mb-0 mt-2 px-2">The number of seconds allowed before a signed request is treated as expired. Default is 300 seconds.</p>
                </div>

                <div class="mb-0 text-start">
                  <label class="ms-2 d-flex align-items-center gap-2">
                    <span>System Code</span>
                    <span class="badge text-bg-danger">required</span>
                  </label>
                  <input v-model="platform_config_form.system" type="text" class="form-control" placeholder="e.g. em_guidance">
                  <p class="small text-muted mb-0 mt-2 px-2">The system identifier sent to the connected platform during launch and DevOps handover.</p>
                </div>
              </div>

              <div v-if="platform_config_step === 4" class="border rounded-4 p-3 bg-light-subtle">
                <div class="mb-3 text-start">
                  <label class="ms-2 d-flex align-items-center gap-2">
                    <span>Routing Rules</span>
                    <span class="badge text-bg-secondary">optional</span>
                  </label>
                  <textarea
                    v-model="platform_config_form.routing_rules_text"
                    class="form-control"
                    rows="5"
                    placeholder="[]"
                  ></textarea>
                  <p class="small text-muted mb-0 mt-2 px-2">Optional advanced routing rules. Leave as [] if no custom routing is required.</p>
                </div>

                <div class="mb-0 text-start">
                  <label class="ms-2">Status</label>
                  <select v-model="platform_config_form.status" class="form-control">
                    <option>Draft</option>
                  </select>
                  <p class="small text-muted mb-0 mt-2 px-2">Draft means the config is saved but should still be tested before being considered ready.</p>
                </div>
              </div>

              <div class="d-flex justify-content-between align-items-center pt-4">
                <div class="d-flex gap-2">
                  <button v-if="platform_config_step > 1" type="button" class="btn btn-outline-dark" @click="previous_step()">
                    Back
                  </button>
                  <button type="button" class="btn btn-outline-secondary" @click="go_back_to_platform_management">
                    Cancel
                  </button>
                </div>

                <button v-if="platform_config_step < 4" type="button" class="btn upload-button text-white" @click="next_step()">
                  Next
                </button>
                <button v-else type="submit" class="btn upload-button text-white" :disabled="saving_platform_config">
                  <span v-if="saving_platform_config" class="spinner-border spinner-border-sm me-2"></span>
                  Save Config
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
